import React, { useState, useEffect } from 'react';
import { LOCApplication } from '../data/mockData';
import { formatCurrency, colors } from '../theme';
import StatusBadge from '../components/StatusBadge';
import api from '../services/api';

const Approvals: React.FC = () => {
  const [showModal, setShowModal] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [pendingLOCs, setPendingLOCs] = useState<LOCApplication[]>([]);
  const [chainData, setChainData] = useState<Record<string, { primary: string; secondary: string; autoApproveLimit: number }>>({});
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionDetail, setRejectionDetail] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, chainsRes] = await Promise.allSettled([
        api.approvals.getPending(),
        api.approvals.getChains(),
      ]);
      if (pendingRes.status === 'fulfilled') {
        const data = pendingRes.value.data || pendingRes.value;
        if (Array.isArray(data)) setPendingLOCs(data);
      }
      if (chainsRes.status === 'fulfilled') {
        const data = chainsRes.value.data || chainsRes.value;
        if (data && typeof data === 'object') setChainData(data);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleConfirm = async () => {
    if (!showModal) return;
    setConfirmLoading(true);
    try {
      if (actionType === 'approve') {
        await api.approvals.approve(showModal, remarks || undefined);
      } else {
        const reason = rejectionDetail || rejectionReason || 'Rejected by admin';
        await api.approvals.reject(showModal, reason);
      }
      setShowModal(null);
      setRemarks('');
      setRejectionReason('');
      setRejectionDetail('');
      fetchData();
    } catch (err: any) {
      alert('Action failed: ' + (err.message || 'Unknown error'));
    } finally {
      setConfirmLoading(false);
    }
  };

  const getSlaRemaining = (deadline: string): { text: string; percent: number; breached: boolean } => {
    const diff = new Date(deadline).getTime() - Date.now();
    if (diff <= 0) return { text: 'SLA BREACHED', percent: 100, breached: true };
    const mins = Math.floor(diff / 60000);
    const totalSla = 30;
    const elapsed = totalSla - mins;
    const percent = Math.min(100, (elapsed / totalSla) * 100);
    if (mins < 60) return { text: `${mins} min remaining`, percent, breached: false };
    return { text: `${Math.floor(mins / 60)}h ${mins % 60}m remaining`, percent, breached: false };
  };

  const modal = showModal ? pendingLOCs.find(l => l.id === showModal) : null;

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>Pending Approvals</h2>
            <p>{pendingLOCs.length} applications awaiting decision</p>
          </div>
        </div>
        <div className="header-right">
          <button className="header-btn secondary" onClick={() => fetchData()}>⟳ Refresh</button>
        </div>
      </div>

      <div className="content">
        {error && <div style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderRadius: 8, margin: '0 0 16px', fontSize: 13, fontWeight: 600 }}>{error}</div>}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>
        ) : pendingLOCs.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-state-icon">✓</div>
                <h4>All Caught Up</h4>
                <p>No pending approvals at the moment</p>
              </div>
            </div>
          </div>
        ) : (
          pendingLOCs.map(loc => {
            const sla = getSlaRemaining(loc.slaDeadline);
            const chain = chainData[loc.category];

            return (
              <div className="card" key={loc.id}>
                <div className="card-body padded">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 800, fontSize: 16 }}>{loc.refNumber}</span>
                        <StatusBadge status={loc.status} />
                        {loc.isEmergency && <span className="badge emergency"><span className="badge-dot" />EMERGENCY</span>}
                      </div>
                      <div style={{ fontSize: 13, color: colors.textSecondary }}>
                        {loc.officerName} ({loc.category}) · {loc.officerCfmsId}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="amount" style={{ fontSize: 20 }}>{formatCurrency(loc.estimatedAmount)}</div>
                      <div style={{ fontSize: 12, color: sla.breached ? colors.error : colors.gold, fontWeight: 700 }}>
                        {sla.text}
                      </div>
                    </div>
                  </div>

                  {/* SLA Bar */}
                  <div className="sla-bar" style={{ marginBottom: 16 }}>
                    <div className={`sla-fill${sla.breached ? ' breach' : ''}`} style={{ width: `${sla.percent}%` }} />
                  </div>

                  <div className="grid-3" style={{ marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Beneficiary</div>
                      <div style={{ fontWeight: 600 }}>{loc.beneficiaryName} ({loc.relation})</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Hospital</div>
                      <div style={{ fontWeight: 600 }}>{loc.hospitalName}, {loc.hospitalCity}</div>
                      <div style={{ fontSize: 12, color: colors.textMuted }}>Tier {loc.hospitalTier} · Match: {loc.matchScore ?? 'N/A'}%</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Treatment</div>
                      <div style={{ fontWeight: 600 }}>{loc.treatment}</div>
                      <div style={{ fontSize: 12, color: colors.textMuted }}>{loc.diagnosis}</div>
                    </div>
                  </div>

                  {/* Approval Chain */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="approval-chain">
                      <div className="chain-step active">Primary: {chain?.primary}</div>
                      <span className="chain-arrow">→</span>
                      <div className="chain-step">Escalation (15 min): {chain?.secondary}</div>
                      <span className="chain-arrow">→</span>
                      <div className="chain-step">Auto-approve: {formatCurrency(chain?.autoApproveLimit || 0)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="header-btn primary" onClick={() => { setShowModal(loc.id); setActionType('approve'); }}>
                        ✓ Approve
                      </button>
                      <button className="action-btn reject" style={{ padding: '8px 16px' }} onClick={() => { setShowModal(loc.id); setActionType('reject'); }}>
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{actionType === 'approve' ? '✓ Approve' : '✕ Reject'} LOC — {modal.refNumber}</h3>
              <button className="modal-close" onClick={() => setShowModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, padding: 12, background: colors.bgSubtle, borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>Officer</div>
                  <div style={{ fontWeight: 600 }}>{modal.officerName}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>Amount</div>
                  <div className="amount">{formatCurrency(modal.estimatedAmount)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>Hospital</div>
                  <div style={{ fontWeight: 600 }}>{modal.hospitalName}</div>
                </div>
              </div>

              {actionType === 'approve' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Payment Amount (80% Advance)</label>
                    <input className="form-input" type="text" defaultValue={formatCurrency(modal.estimatedAmount * 0.8)} readOnly />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Remarks (Optional)</label>
                    <textarea className="form-textarea" placeholder="Add approval remarks..." value={remarks} onChange={e => setRemarks(e.target.value)} />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Rejection Reason</label>
                    <select className="form-select" value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}>
                      <option value="">Select reason...</option>
                      <option>Hospital verification failed</option>
                      <option>Name match score below threshold</option>
                      <option>Duplicate application</option>
                      <option>Ineligible beneficiary</option>
                      <option>Insufficient documentation</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Detailed Reason</label>
                    <textarea className="form-textarea" placeholder="Provide detailed rejection reason..." value={rejectionDetail} onChange={e => setRejectionDetail(e.target.value)} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="header-btn secondary" onClick={() => setShowModal(null)}>Cancel</button>
              <button className={`header-btn ${actionType === 'approve' ? 'primary' : ''}`}
                style={actionType === 'reject' ? { background: colors.error, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' } : {}}
                disabled={confirmLoading}
                onClick={handleConfirm}>
                {confirmLoading ? 'Processing...' : (actionType === 'approve' ? '✓ Confirm Approval' : '✕ Confirm Rejection')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Approvals;
